import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useChannelStore } from '@/store/channel'
import { getAvailableSwitcherLanguages, isUrlLang, toUrlLang } from '@/i18n/languages'
import { useChangeLanguage } from '@/hooks/useChangeLanguage'
import { switchLangPrefix } from '@/utils/localizedPath'

/**
 * Root of every storefront route: `/:lang/...`.
 *
 * The URL prefix is the source of truth for the language. A valid prefix that
 * differs from the active language is applied (cookie, store, i18n bundle,
 * Apollo refetch); anything else — legacy unprefixed links such as backend
 * emails (`/password-reset?token=...`) or a language the channel does not
 * serve — is redirected to the same path under the active language.
 */
export default function LanguageRoute() {
  const { lang } = useParams<{ lang: string }>()
  const location = useLocation()
  const availableLanguageCodes = useChannelStore((store) => store.availableLanguageCodes)
  const currentLanguageCode = useChannelStore((store) => store.currentLanguageCode)
  const changeLanguage = useChangeLanguage()

  const options = getAvailableSwitcherLanguages(availableLanguageCodes)
  const currentUrlLang = toUrlLang(currentLanguageCode)

  let targetLanguageCode: string | null = null
  if (isUrlLang(lang)) {
    if (options.length === 0) {
      // Channel lists no languages: accept any supported prefix as-is.
      targetLanguageCode = lang === currentUrlLang ? currentLanguageCode ?? lang! : lang!
    } else {
      targetLanguageCode = options.find((option) => option.catalogCode === lang)?.vendureCode ?? null
    }
  }

  useEffect(() => {
    if (targetLanguageCode && targetLanguageCode !== currentLanguageCode) {
      void changeLanguage(targetLanguageCode)
    }
  }, [targetLanguageCode, currentLanguageCode, changeLanguage])

  if (!targetLanguageCode) {
    const redirectLang =
      options.length === 0 || options.some((option) => option.catalogCode === currentUrlLang)
        ? currentUrlLang
        : options[0].catalogCode
    return (
      <Navigate
        to={`${switchLangPrefix(location.pathname, redirectLang)}${location.search}${location.hash}`}
        state={location.state}
        replace
      />
    )
  }

  // Hold children until the store follows the URL, so their queries and links
  // use the new language instead of firing once with the old one.
  if (targetLanguageCode !== currentLanguageCode) {
    return null
  }

  return <Outlet />
}
